import { createRootRoute, Outlet, useRouterState } from '@tanstack/react-router'
import { lazy, Suspense, useEffect } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { UpdateBanner } from '../components/UpdateBanner'

// Keeps <link rel="canonical"> pointing at the CURRENT route. A single
// static canonical in index.html would declare every page a duplicate of
// the homepage, which de-indexes /pricing and /docs.
function CanonicalSync() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  useEffect(() => {
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (!link) {
      link = document.createElement('link')
      link.rel = 'canonical'
      document.head.appendChild(link)
    }
    const path = pathname === '/' ? '/' : pathname.replace(/\/+$/, '')
    link.href = `https://folioworth.com${path}`
  }, [pathname])
  return null
}

// Lazy load devtools only in development
const TanStackRouterDevtools =
  import.meta.env.MODE === 'production'
    ? () => null
    : lazy(() =>
      import('@tanstack/router-devtools').then((res) => ({
        default: res.TanStackRouterDevtools,
      }))
    )

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: (failureCount, error: any) => {
        if (error?.status === 404) return false
        return failureCount < 3
      },
    },
  },
})

// Unknown paths must not look like real pages to a crawler. The SPA serves
// the same shell with HTTP 200 for every path, which Google flags as a
// "soft 404"; a robots noindex injected by the not-found route converts
// that into an intentional exclusion without touching nginx route lists.
function NotFound() {
  useEffect(() => {
    const meta = document.createElement('meta')
    meta.name = 'robots'
    meta.content = 'noindex'
    document.head.appendChild(meta)
    return () => {
      document.head.removeChild(meta)
    }
  }, [])
  return (
    <div style={{ padding: '4rem', textAlign: 'center', fontFamily: 'Inter, sans-serif' }}>
      <h1>Page not found</h1>
      <p>
        That page does not exist. <a href="/">Back to FolioWorth</a>
      </p>
    </div>
  )
}

export const Route = createRootRoute({
  notFoundComponent: NotFound,
  component: () => (
    <QueryClientProvider client={queryClient}>
      <CanonicalSync />
      <Outlet />
      <UpdateBanner />
      <Suspense>
        <TanStackRouterDevtools />
      </Suspense>
    </QueryClientProvider>
  ),
})
